import React, { useEffect, useRef, useState } from 'react';
import { PropTypes, shape, arrayOf, oneOf } from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import ProductFilter from '../../molecules/ProductFilter';
import { getAppliedFilters } from '../productListingHelper';
import GlobalNavigationMenuDesktopL2 from '../../molecules/GlobalNavigationMenuDesktopL2/views/GlobalNavigationPanel.container';
import { getViewportInfo } from '../../../../../../utils';
import LoadedProductsCount from '../../molecules/LoadedProductsCount/views/LoadedProductsCount.view';
import { LoadCount, Section, CategoryHeading } from './SideBar.style';
import style from './styles/SideBarCss.styles';

const SideBar = ({
  className,
  filtersMaps,
  totalProductsCount,
  initialValues,
  filtersLength,
  onSubmit,
  formValues,
  getProducts,
  isFilterBy,
  isLoadingMore,
  isABTestForStickyFilter,
  isBopisFilterOn,
  updateFormValue,
  categoryNavData = {},
  categoryPlp,
  selectedFilterOption,
  breadCrumbs,
  isBopisFilterEnabled,
  setBopisFilterStateActn,
  defaultStoreData,
  resetSuggestedStores,
  isPaginationViewEnabled,
  selectedPageProductCount,
  productLimitPerPage,
  selectedPage,
  tabPriceRange,
  setTabPriceRange,
  trackClickAnalyticsBopisFilter,
  newFilterTrackAnalytics,
  bopisNullResult,
  isSizeGroupFilterEnabled,
  isOutfitPLPEnabled,
  isCustomerRatingFilterWebEnabled,
  isTileRedesignAB,
  isHPLP,
  isHeaderScrollViewDisabled,
  isGlobalHeaderRedesignEnabled,
  isFeatureBrandView,
  isFeatureBrandEnabled,
  setPriceFilterFlagActn,
  priceFilterFLag,
}) => {
  const hasMounted = useHasMounted();
  const isMobile = hasMounted && getViewportInfo()?.isMobile;
  const { extraConfig } = React.useContext(LabelsContext);
  const { isHeaderStickyClassPresent, stickyHeaderHeight, headerMiddleNavHeight } =
    extraConfig || {};

  if (isMobile && window?.document?.querySelectorAll('.filter-label-expanded').length === 1) {
    window?.document?.querySelector('.filter-label-expanded')?.scrollIntoView();
  }
  const sidebarRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);
  // send trackwidth to slider | clientwidth - padding;
  useEffect(() => {
    setTrackWidth(sidebarRef?.current?.clientWidth - 38);
  }, [sidebarRef.current]);

  const scrollSidebarToVisibleView = (filterPositionRef) => {
    setTimeout(() => {
      return filterPositionRef?.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 100);
  };

  const appliedFilters = getAppliedFilters(initialValues, filtersMaps);
  const isDesktop = hasMounted && getViewportInfo()?.isDesktop;
  return (
    <Section
      className={`${className} sidebar filter-redesign`}
      bopisNullResult={bopisNullResult}
      isHeaderScrollViewDisabled={!isHeaderScrollViewDisabled}
      isHeaderStickyClassPresent={isHeaderStickyClassPresent}
      stickyHeaderHeight={stickyHeaderHeight}
      ref={sidebarRef}
      headerMiddleNavHeight={headerMiddleNavHeight}
      isGlobalHeaderRedesignEnabled={isGlobalHeaderRedesignEnabled}
    >
      <div className="navigation-main">
        {categoryNavData?.navTree && categoryPlp && isDesktop && !isHPLP && (
          <>
            <CategoryHeading>Category</CategoryHeading>
            <GlobalNavigationMenuDesktopL2
              navigationTree={categoryNavData?.navTree}
              activeCategoryIds={categoryNavData?.currentNavIds}
              newFilterTrackAnalytics={newFilterTrackAnalytics}
              isOutfitPLPEnabled={isOutfitPLPEnabled}
            />
          </>
        )}
      </div>
      {getViewportInfo()?.isDesktop && totalProductsCount > 0 && !isHPLP && (
        <LoadCount categoryPlp={categoryPlp} className="filter-product-count">
          <LoadedProductsCount
            totalProductsCount={totalProductsCount}
            breadCrumbs={breadCrumbs}
            isPaginationViewEnabled={isPaginationViewEnabled}
            selectedPageProductCount={selectedPageProductCount}
            productLimitPerPage={productLimitPerPage}
            selectedPage={selectedPage}
          />
        </LoadCount>
      )}
      <ProductFilter
        filtersMaps={filtersMaps}
        totalProductsCount={totalProductsCount}
        initialValues={initialValues}
        filtersLength={filtersLength}
        onSubmit={onSubmit}
        formValues={formValues}
        getProducts={getProducts}
        isFilterBy={isFilterBy}
        isLoadingMore={isLoadingMore}
        isABTestForStickyFilter={isABTestForStickyFilter}
        isBopisFilterOn={isBopisFilterOn}
        updateFormValue={updateFormValue}
        categoryNavData={categoryNavData}
        categoryPlp={categoryPlp}
        newPriceRange={filtersMaps?.newPriceRange}
        initialTrackWidth={trackWidth}
        selectedFilterOption={selectedFilterOption}
        appliedFilters={appliedFilters}
        sidebarRef={sidebarRef}
        scrollSidebarToVisibleView={scrollSidebarToVisibleView}
        setBopisFilterStateActn={setBopisFilterStateActn}
        defaultStoreData={defaultStoreData}
        resetSuggestedStores={resetSuggestedStores}
        isBopisFilterEnabled={isBopisFilterEnabled}
        tabPriceRange={tabPriceRange}
        setTabPriceRange={setTabPriceRange}
        trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
        newFilterTrackAnalytics={newFilterTrackAnalytics}
        isSizeGroupFilterEnabled={isSizeGroupFilterEnabled}
        isCustomerRatingFilterWebEnabled={isCustomerRatingFilterWebEnabled}
        isTileRedesignAB={isTileRedesignAB}
        isHPLP={isHPLP}
        isFeatureBrandView={isFeatureBrandView}
        isFeatureBrandEnabled={isFeatureBrandEnabled}
        setPriceFilterFlagActn={setPriceFilterFlagActn}
        priceFilterFLag={priceFilterFLag}
      />
    </Section>
  );
};

SideBar.propTypes = {
  categoryNavData: shape({ navTree: oneOf([null, {}]), currentNavIds: arrayOf({}) }).isRequired,
  filtersMaps: PropTypes.shape({ newPriceRange: PropTypes.shape({}) }),
  totalProductsCount: PropTypes.string,
  initialValues: PropTypes.shape({}),
  filtersLength: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number])),
  onSubmit: PropTypes.func.isRequired,
  getProducts: PropTypes.func.isRequired,
  isFilterBy: PropTypes.bool,
  favoriteSortingParams: PropTypes.shape({}),
  formValues: PropTypes.shape({}),
  isLoadingMore: PropTypes.bool,
  isBopisFilterOn: PropTypes.bool,
  isABTestForStickyFilter: PropTypes.bool,
  updateFormValue: PropTypes.func.isRequired,
  defaultStore: PropTypes.shape({}),
  categoryPlp: PropTypes.bool,
  selectedFilterOption: PropTypes.string,
  isSizeGroupFilterEnabled: PropTypes.bool,
  isHeaderScrollViewDisabled: PropTypes.bool,
};

SideBar.defaultProps = {
  filtersMaps: {
    newPriceRange: {},
  },
  totalProductsCount: '0',
  initialValues: {},
  filtersLength: {},
  isFilterBy: true,
  favoriteSortingParams: null,
  formValues: {},
  isLoadingMore: false,
  isBopisFilterOn: false,
  isABTestForStickyFilter: false,
  defaultStore: {},
  categoryPlp: false,
  selectedFilterOption: '',
  isSizeGroupFilterEnabled: false,
  isHeaderScrollViewDisabled: false,
};

export default withStyles(SideBar, style);
export { SideBar as SideBarVanilla };
